"use client";

import React, { useState, useEffect, useRef } from "react";
import { PACKAGES } from "./packages";
import {
  LOCATIONS,
  LOCATION_LIST,
  locationDaysLabel,
  type BallancerLocationId,
} from "@lib/ballancerLocations";
import { track } from "@lib/track";
import MarketingOptIn from "@/app/components/MarketingOptIn";

const PREFERRED_TIMES = ["10:00AM", "11:00AM", "12:00PM"];

/** Must match BALLANCER_HORIZON_DAYS server-side. */
const HORIZON_DAYS = parseInt(process.env.NEXT_PUBLIC_BALLANCER_HORIZON_DAYS ?? "56", 10);

/**
 * How long a fetched availability snapshot is trusted. Slots used to be cached
 * for the life of the page, so an hour taken while the visitor was filling in
 * the form still rendered as free. The server rejects those now, but re-checking
 * keeps the user from picking a slot they can't have.
 */
const SLOTS_TTL_MS = 60 * 1000;

const toMonthStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

type DayStatus = "loading" | "available" | "none";

interface Props {
  customerPresent?: boolean; // supplier context: true = redirect, false = email link, undefined = self-service
  /**
   * Pre-selects a site. The picker stays visible and overridable on purpose: Stocks House
   * staff are usually booking into their own venue, so defaulting saves a click and lands
   * them straight on the right days — but a client standing at their desk may well want a
   * Sunday, which only runs at Nicola's. Locking it would make that booking impossible.
   */
  defaultLocation?: BallancerLocationId;
}

type BookingDone = { mode: "link_sent"; email: string; emailError?: boolean; paymentUrl?: string };

export default function BodyBallancerBookingClient({ customerPresent, defaultLocation }: Props = {}) {
  const [bookingDone, setBookingDone] = useState<BookingDone | null>(null);

  // Package selection
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const selectedPackage = PACKAGES.find((p) => p.id === selectedPackageId) ?? null;

  // Calendar state
  const bounds = useRef<{ min: Date; max: Date; currentMonth: string; maxMonth: string } | null>(null);
  if (!bounds.current) {
    const t = new Date();
    const tomorrow = new Date(t); tomorrow.setDate(t.getDate() + 1); tomorrow.setHours(0, 0, 0, 0);
    const openFromEnv = process.env.NEXT_PUBLIC_BALLANCER_OPEN_FROM;
    const openFromDate = openFromEnv ? new Date(openFromEnv + "T00:00:00") : null;
    const min = openFromDate && openFromDate > tomorrow ? openFromDate : tomorrow;
    // Bookable window is a rolling horizon rather than "five months out", so the
    // whole of it fits in one cached availability response.
    const max = new Date(min); max.setDate(min.getDate() + HORIZON_DAYS);
    bounds.current = {
      min,
      max,
      currentMonth: toMonthStr(min),
      maxMonth: toMonthStr(max),
    };
  }
  const { min: minDate, max: maxDate, currentMonth, maxMonth } = bounds.current;

  // Which site the visitor is booking into. Body Ballancer runs at two sites on a fixed
  // weekly rota, so location is a property of the DATE rather than a separate resource —
  // but asking first is what stops someone who can only reach one of them scanning a
  // calendar to work out which days are even possible.
  const [selectedLocation, setSelectedLocation] = useState<BallancerLocationId | null>(
    defaultLocation ?? null,
  );
  const locationByDay = useRef<Record<string, BallancerLocationId>>({});

  const [calendarMonth, setCalendarMonth] = useState(currentMonth);
  const [dayStatus, setDayStatus] = useState<Record<string, DayStatus>>({});
  const batchSlotsCache = useRef<Record<string, string[]>>({});
  const batchFetchedAt = useRef(0);
  const slotsCache = useRef<Record<string, { slots: string[]; at: number }>>({});
  const pendingQuickSlotRef = useRef<string | null>(null);
  const selectedSlotRef = useRef<string | null>(null);
  const [slotsNonce, setSlotsNonce] = useState(0);

  // Slot selection
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlotRaw, setSelectedSlotRaw] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [quickBookClicked, setQuickBookClicked] = useState<string | null>(null);

  // Form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const scrollToFormRef = useRef(false);

  // Fetch the whole booking horizon once. This used to run per displayed month
  // and cost three sequential Graph calls each time, so paging through months
  // was both slow and a reliable way to trip Graph throttling.
  useEffect(() => {
    const toMark: string[] = [];
    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      toMark.push(toISODate(d));
    }
    if (toMark.length === 0) return;

    setDayStatus(Object.fromEntries(toMark.map((d) => [d, "loading" as DayStatus])));

    let cancelled = false;

    fetch("/api/body-ballancer/available-days", { method: "POST" })
      .then((r) => r.json())
      .then(({ availableDays, slotsByDay, locationByDay: locs }: {
        availableDays: string[];
        slotsByDay: Record<string, string[]>;
        locationByDay: Record<string, BallancerLocationId>;
      }) => {
        if (cancelled) return;
        if (locs) locationByDay.current = locs;
        const available = new Set(availableDays ?? []);
        setDayStatus(Object.fromEntries(
          toMark.map((d) => [d, available.has(d) ? "available" : "none"] as const),
        ));
        if (slotsByDay) {
          for (const [day, slots] of Object.entries(slotsByDay)) {
            batchSlotsCache.current[day] = slots;
          }
        }
        batchFetchedAt.current = Date.now();
        // Land the visitor on the first month that actually has availability — at their
        // chosen site if they have already picked one, since the first free day overall
        // may well be at the other location.
        const firstFree = (availableDays ?? []).find(
          (d) => toMark.includes(d) && (!selectedLocation || locs?.[d] === selectedLocation),
        );
        if (firstFree) setCalendarMonth(firstFree.slice(0, 7));
      })
      .catch(() => {
        if (cancelled) return;
        setDayStatus(Object.fromEntries(toMark.map((d) => [d, "none" as DayStatus])));
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keeps the current selection readable inside the async revalidation below
  // without making it a dependency of the effect (which would refire it).
  useEffect(() => { selectedSlotRef.current = selectedSlotRaw; }, [selectedSlotRaw]);

  // Load slots when a day is selected.
  //
  // Stale-while-revalidate: whatever we already know is rendered immediately, so
  // picking a date never blanks the list, and a background re-check corrects it
  // if anything was taken since. Only a day we've never fetched shows a spinner.
  useEffect(() => {
    if (!selectedDay) return;
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);

    const pendingSlot = pendingQuickSlotRef.current;
    pendingQuickSlotRef.current = null;

    const applyPending = (slots: string[]) => {
      if (pendingSlot && slots.includes(pendingSlot)) applySlot(pendingSlot);
    };

    const entry = slotsCache.current[selectedDay];
    const cached = entry?.slots ?? batchSlotsCache.current[selectedDay];
    const cachedAt = entry?.at ?? batchFetchedAt.current;
    const isFresh = cached !== undefined && Date.now() - cachedAt < SLOTS_TTL_MS;

    if (cached !== undefined) {
      setAvailableSlots(cached);
      applyPending(cached);
      if (isFresh) return;
    } else {
      setAvailableSlots([]);
      setSlotsLoading(true);
    }

    let cancelled = false;

    fetch("/api/body-ballancer/availability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date: selectedDay }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const slots = Array.isArray(data.slots) ? data.slots : [];
        slotsCache.current[selectedDay] = { slots, at: Date.now() };
        batchSlotsCache.current[selectedDay] = slots;
        setAvailableSlots(slots);
        if (cached === undefined) {
          applyPending(slots);
        } else if (selectedSlotRef.current && !slots.includes(selectedSlotRef.current)) {
          // Their pick went while they were looking at it — drop it now rather
          // than letting them fill the form and hit a 409 on submit.
          setSelectedSlotRaw(null);
          setSelectedStartTime(null);
        }
      })
      .catch(() => { if (!cancelled && cached === undefined) setAvailableSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });

    return () => { cancelled = true; };
  }, [selectedDay, slotsNonce]);

  useEffect(() => {
    if (selectedSlotRaw && scrollToFormRef.current) {
      scrollToFormRef.current = false;
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedSlotRaw]);

  // Preselect the special offer when arriving from the offer pill (self-service only)
  useEffect(() => {
    if (customerPresent !== undefined) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("offer") && PACKAGES.some((p) => p.id === "detox-combo")) {
      setSelectedPackageId("detox-combo");
    }
  }, [customerPresent]);

  function applySlot(raw: string) {
    const match = raw.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
    if (!match) return;
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    else if (period === "AM" && h === 12) h = 0;
    setSelectedSlotRaw(raw);
    setSelectedStartTime(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
    setQuickBookClicked(null);
  }

  function handleSlotSelect(slot: string) {
    scrollToFormRef.current = true;
    applySlot(slot);
  }

  function handleQuickBook(day: string, slot: string) {
    if (selectedDay === day && selectedSlotRaw === slot) return;
    if (quickBookClicked === `${day}|${slot}`) return;
    setQuickBookClicked(`${day}|${slot}`);
    pendingQuickSlotRef.current = slot;
    scrollToFormRef.current = true;
    setCalendarMonth(day.substring(0, 7));
    setSelectedDay(day);
    try { track("quick_book", { label: `ballancer | ${day} ${slot}`, component: "BodyBallancerBookingClient" }, { event: "quick_book", label: `${day} ${slot}`, component: "BodyBallancerBookingClient" }); } catch {}
  }

  function chooseLocation(id: BallancerLocationId) {
    if (id === selectedLocation) return;
    setSelectedLocation(id);
    resetCalendar();
    // The chosen site's first free day is very likely in a different month from the
    // other site's, so follow it rather than leaving the visitor on an empty month.
    const first = Object.keys(dayStatus)
      .filter((d) => dayStatus[d] === "available" && locationByDay.current[d] === id)
      .sort()[0];
    if (first) setCalendarMonth(first.slice(0, 7));
    // Third argument is the one that is actually enqueued — the first two are ignored by
    // track(). Label is the location id, matching the component-level convention (a stable
    // identifier, as venue_sale_initiated uses the supplier slug).
    try {
      track(
        "ballancer_location_select",
        { label: id, component: "BodyBallancerBookingClient" },
        { event: "ballancer_location_select", label: id, component: "BodyBallancerBookingClient" },
      );
    } catch {}
  }

  function resetCalendar() {
    setSelectedDay(null);
    setAvailableSlots([]);
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);
    setQuickBookClicked(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    if (!selectedPackageId || !selectedDay || !selectedSlotRaw || !selectedStartTime) return;
    if (!name.trim() || !email.includes("@")) return;
    if (selectedPackageId !== "single" && !dob) return;

    setIsSubmitting(true);
    try {
      track(
        "ballancer_booking_submit",
        { label: `${selectedDay} ${selectedSlotRaw} | ${email}`, component: "BodyBallancerBookingClient" },
        { event: "ballancer_booking_submit", label: `${selectedDay} ${selectedSlotRaw}`, component: "BodyBallancerBookingClient" }
      );
    } catch {}

    try {
      const res = await fetch("/api/body-ballancer/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: selectedDay,
          startTime: selectedStartTime,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          packageId: selectedPackageId,
          ...(selectedPackageId !== "single" && dob ? { dob } : {}),
          ...(customerPresent !== undefined ? { customerPresent } : {}),
          marketingOptIn,
        }),
      });

      const data = await res.json().catch(() => ({}));

      // Someone else took the slot between it rendering and this request. Drop
      // the stale view and send them back to pick again rather than failing hard.
      if (res.status === 409 || data.code === "slot_unavailable") {
        slotsCache.current = {};
        batchSlotsCache.current = {};
        batchFetchedAt.current = 0;
        setSelectedSlotRaw(null);
        setSelectedStartTime(null);
        setSlotsNonce((n) => n + 1); // caches cleared above — re-fetch this day
        alert("Sorry — that time was just booked by someone else. Please choose another slot.");
        return;
      }

      if (!res.ok || !data.ok) throw new Error(data.error ?? "Failed to initiate booking");

      if (data.mode === "link_sent") {
        setBookingDone({ mode: "link_sent", email: email.trim().toLowerCase(), emailError: data.emailError, paymentUrl: data.paymentUrl });
        return;
      }

      // mode === "redirect" or self-service (no mode, just paymentUrl)
      window.location.href = data.paymentUrl;
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const monthIsLoading = Object.values(dayStatus).some((s) => s === "loading");

  // Only days at the chosen site are offered. The rota assigns each weekday to exactly
  // one location, so this is a filter on the same single availability response rather
  // than a second fetch.
  const atSelectedLocation = (day: string) =>
    !selectedLocation || locationByDay.current[day] === selectedLocation;

  const availableDaysInMonth = Object.keys(dayStatus)
    .filter((d) => d.startsWith(calendarMonth) && dayStatus[d] === "available" && atSelectedLocation(d))
    .sort();

  /** Soonest available day at a location, for the picker cards. */
  const nextDayAt = (id: BallancerLocationId): string | null =>
    Object.keys(dayStatus)
      .filter((d) => dayStatus[d] === "available" && locationByDay.current[d] === id)
      .sort()[0] ?? null;

  const calendarMonthLabel = (() => {
    const [y, m] = calendarMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
  })();

  // Build quick-book options: up to 3 soonest available slots across days
  const quickBookOptions: { day: string; slot: string; dayLabel: string; timeLabel: string }[] = [];
  {
    const availableDays = Object.keys(dayStatus)
      .filter((d) => dayStatus[d] === "available" && atSelectedLocation(d))
      .sort()
      .slice(0, 14);
    for (const day of availableDays) {
      const cached = batchSlotsCache.current[day] ?? slotsCache.current[day]?.slots;
      if (!cached?.length) continue;
      const slot = PREFERRED_TIMES.find((t) => cached.includes(t)) ?? cached[0];
      const d = new Date(day + "T00:00:00");
      const dayLabel = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      const timeLabel = slot.replace(":00", "").toLowerCase();
      quickBookOptions.push({ day, slot, dayLabel, timeLabel });
      if (quickBookOptions.length >= 3) break;
    }
  }

  const friendlyDay = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  if (bookingDone) {
    return (
      <div className="rounded-3xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm p-6 md:p-8">
        {bookingDone.emailError ? (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 text-center">
            <p className="text-xl font-semibold text-amber-800 mb-2">Slot reserved — email failed</p>
            <p className="text-sm text-amber-700 mb-3">
              The slot is held in the calendar but the payment email could not be sent. Share this link manually:
            </p>
            <a href={bookingDone.paymentUrl} target="_blank" rel="noreferrer" className="text-sm underline text-amber-800 break-all">
              {bookingDone.paymentUrl}
            </a>
            <button onClick={() => setBookingDone(null)} className="mt-4 block mx-auto text-sm underline text-amber-700">
              Book another
            </button>
          </div>
        ) : (
          <div className="rounded-2xl bg-green-50 border border-green-200 p-6 text-center">
            <p className="text-xl font-semibold text-green-800 mb-2">Slot reserved</p>
            <p className="text-sm text-green-700">
              Payment link emailed to <strong>{bookingDone.email}</strong>. Calendar invite will be sent once they pay.
            </p>
            <button onClick={() => setBookingDone(null)} className="mt-4 text-sm underline text-green-700">
              Book another
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm overflow-hidden">

      {/* ── Package selection ── */}
      <div className="p-6 md:p-8 border-b border-[var(--border-on-light)]">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-brand)] mb-1">
          Choose your session
        </p>
        <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
          Each session is 45 minutes. Blocks are paid upfront — remaining sessions booked individually after your first.
        </p>
        <div className="space-y-3">
          {PACKAGES.map((pkg) => (
            <label
              key={pkg.id}
              className={`flex items-start justify-between gap-4 rounded-xl border px-5 py-4 cursor-pointer transition-colors
                ${selectedPackageId === pkg.id
                  ? "border-[var(--text-brand)] bg-[var(--bg-brand-tint)]"
                  : "border-[var(--border-on-light)] bg-[var(--bg-surface)] hover:border-[var(--text-brand)]"}`}
            >
              <span className="flex items-start gap-3">
                <input
                  type="radio"
                  name="package"
                  className="accent-[var(--text-brand)] mt-1 shrink-0"
                  checked={selectedPackageId === pkg.id}
                  onChange={() => { setSelectedPackageId(pkg.id); resetCalendar(); }}
                />
                <span>
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[var(--text-on-light)]">{pkg.label}</span>
                    {pkg.badge && (
                      <span className="inline-block rounded-full bg-[var(--accent)] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                        {pkg.badge}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-[var(--text-on-light-muted)] mt-0.5">{pkg.description}</span>
                  {pkg.includes && (
                    <span className="block text-xs text-[var(--text-brand)] mt-1">
                      {pkg.includes}
                      {pkg.includesHref && (
                        <>
                          {" "}
                          <a
                            href={pkg.includesHref}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="underline underline-offset-2 whitespace-nowrap"
                          >
                            See what&apos;s inside →
                          </a>
                        </>
                      )}
                    </span>
                  )}
                </span>
              </span>
              <span className="text-base font-bold text-[var(--accent)] shrink-0">£{pkg.price}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Location ──
          Asked BEFORE the calendar rather than derived silently from whichever date is
          picked: the two sites run on a fixed weekly rota (Sun–Wed / Thu–Sat), so someone
          who can only travel to one of them would otherwise have to work out which days
          are theirs by trial and error. Hidden when the caller fixes the site (supplier
          portal). Street addresses are deliberately absent — Nicola's is private and both
          are sent in the confirmation email. */}
      {selectedPackageId && (
        <div className="p-6 md:p-8 border-b border-[var(--border-on-light)]">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-brand)] mb-1">
            Choose your location
          </p>
          <p className="text-xs text-[var(--text-on-light-muted)] mb-5">
            We run Body Ballancer from four places. Pick whichever suits you — the full
            address is sent with your confirmation.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {LOCATION_LIST.map((loc) => {
              const isSelected = selectedLocation === loc.id;
              const next = nextDayAt(loc.id);
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => chooseLocation(loc.id)}
                  aria-pressed={isSelected}
                  className={[
                    "text-left rounded-xl border px-5 py-4 transition-colors",
                    isSelected
                      ? "border-[var(--text-brand)] bg-[var(--bg-brand-tint)]"
                      : "border-[var(--border-on-light)] bg-[var(--bg-surface)] hover:border-[var(--text-brand)]",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold text-[var(--text-on-light)]">{loc.name}</span>
                  <span className="block text-xs text-[var(--text-on-light-muted)] mt-0.5">{loc.area}</span>
                  <span className="block text-xs text-[var(--text-brand)] mt-1.5 font-medium">
                    {locationDaysLabel(loc.id)}
                  </span>
                  <span className="block text-[11px] text-[var(--text-on-light-muted)] mt-1">
                    {monthIsLoading
                      ? "Checking availability…"
                      : next
                      ? `Next available ${new Date(next + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`
                      : "No availability in the next few weeks"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Scheduling ── */}
      {selectedPackageId && selectedLocation && (
        <div className="p-6 md:p-8">

          {/* Quick book strip */}
          {(monthIsLoading && quickBookOptions.length === 0) ? (
            <div className="mb-6">
              <p className="text-xs font-semibold text-[var(--text-on-light-muted)] uppercase tracking-wider mb-2">Next available</p>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[58px] w-24 rounded-2xl bg-[var(--bg-surface)] animate-pulse" />
                ))}
              </div>
            </div>
          ) : quickBookOptions.length > 0 ? (
            <div className="mb-6">
              <p className="text-xs font-semibold text-[var(--text-on-light-muted)] uppercase tracking-wider mb-2">Next available</p>
              <div className="flex flex-wrap gap-2">
                {quickBookOptions.map(({ day, slot, dayLabel, timeLabel }) => {
                  const isActive = (selectedDay === day && selectedSlotRaw === slot) || quickBookClicked === `${day}|${slot}`;
                  const isDisabled = quickBookClicked !== null && quickBookClicked !== `${day}|${slot}`;
                  return (
                    <button
                      key={`${day}-${slot}`}
                      type="button"
                      onClick={() => handleQuickBook(day, slot)}
                      disabled={isDisabled}
                      className={[
                        "flex flex-col items-center px-4 py-2.5 rounded-2xl text-sm border transition-all",
                        isDisabled
                          ? "border-[var(--border-on-light)] text-[var(--text-on-light-muted)] cursor-not-allowed opacity-40"
                          : isActive
                          ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                          : "bg-[var(--bg-surface)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)] hover:shadow-sm",
                      ].join(" ")}
                    >
                      <span className="font-semibold leading-tight">{timeLabel}</span>
                      <span className={["text-xs leading-tight mt-0.5", isActive ? "text-white/80" : "text-[var(--text-on-light-muted)]"].join(" ")}>{dayLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-[var(--border-on-light)]" />
            <span className="text-xs text-[var(--text-on-light-muted)] whitespace-nowrap">or choose a date</span>
            <div className="flex-1 h-px bg-[var(--border-on-light)]" />
          </div>

          {/* Month calendar */}
          <div className="rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] p-4 mb-5">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => {
                  const [y, m] = calendarMonth.split("-").map(Number);
                  setCalendarMonth(toMonthStr(new Date(y, m - 2, 1)));
                  resetCalendar();
                }}
                disabled={calendarMonth <= currentMonth}
                className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--text-on-light-muted)] text-xl disabled:opacity-20 hover:bg-[var(--border-on-light)] transition"
                aria-label="Previous month"
              >‹</button>

              <div className="text-center">
                <p className="text-sm font-semibold text-[var(--text-on-light)]">{calendarMonthLabel}</p>
                {monthIsLoading && (
                  <p className="text-[11px] text-[var(--text-on-light-muted)] animate-pulse mt-0.5">Checking availability…</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  const [y, m] = calendarMonth.split("-").map(Number);
                  setCalendarMonth(toMonthStr(new Date(y, m, 1)));
                  resetCalendar();
                }}
                disabled={calendarMonth >= maxMonth}
                className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--text-on-light-muted)] text-xl disabled:opacity-20 hover:bg-[var(--border-on-light)] transition"
                aria-label="Next month"
              >›</button>
            </div>

            {monthIsLoading ? (
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-9 w-20 rounded-full bg-[var(--border-on-light)] animate-pulse" />
                ))}
              </div>
            ) : availableDaysInMonth.length === 0 ? (
              <p className="text-sm text-[var(--text-on-light-muted)] text-center py-2">No available dates this month.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableDaysInMonth.map((day) => {
                  const d = new Date(day + "T00:00:00");
                  const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                  const isSelected = day === selectedDay;
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        // Deliberately no setAvailableSlots([]) — the load effect renders
                        // this day's cached slots immediately, and clears them itself when
                        // nothing is cached. Blanking here forces an empty frame per click.
                        setSelectedDay(day);
                        setSelectedSlotRaw(null);
                        setSelectedStartTime(null);
                        setQuickBookClicked(null);
                      }}
                      className={[
                        "px-3 py-1.5 rounded-full text-sm font-medium border transition-all",
                        isSelected
                          ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                          : "bg-[var(--bg-card)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)] hover:shadow-sm",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Time slots */}
          {selectedDay && (
            slotsLoading ? (
              <p className="text-sm text-[var(--text-brand)] animate-pulse mb-5">Loading times…</p>
            ) : availableSlots.length > 0 ? (
              <div className="mb-5">
                <p className="text-sm font-medium text-[var(--text-on-light)] mb-1">{friendlyDay(selectedDay)}</p>
                {selectedLocation && (
                  <p className="text-xs text-[var(--text-on-light-muted)] mb-3">
                    at {LOCATIONS[selectedLocation].name}, {LOCATIONS[selectedLocation].area}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => handleSlotSelect(slot)}
                      aria-pressed={selectedSlotRaw === slot}
                      className={[
                        "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                        selectedSlotRaw === slot
                          ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                          : "bg-[var(--bg-card)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)]",
                      ].join(" ")}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
                No times available on this day — please pick another date
                {selectedLocation ? ` at ${LOCATIONS[selectedLocation].name}` : ""}.
              </p>
            )
          )}

          {/* Details form */}
          {selectedSlotRaw && (
            <form onSubmit={handleSubmit} className="mt-2" ref={formRef as React.RefObject<HTMLFormElement>}>
              <div className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-surface)] p-4 md:p-6 mb-5">
                <h3 className="text-sm font-semibold text-[var(--text-on-light)] mb-4">Your details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-on-light)] mb-1">
                      Full name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full rounded-xl border border-[var(--border-on-light)] px-3 py-2 text-sm text-[var(--text-on-light)] bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-on-light)] mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-[var(--border-on-light)] px-3 py-2 text-sm text-[var(--text-on-light)] bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
                    />
                  </div>
                </div>
                {selectedPackageId !== "single" && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-[var(--text-on-light)] mb-1">
                      Date of birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      className="w-full sm:w-48 rounded-xl border border-[var(--border-on-light)] px-3 py-2 text-sm text-[var(--text-on-light)] bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
                    />
                    <p className="mt-1 text-xs text-[var(--text-on-light-muted)]">Used to verify your identity when booking remaining sessions.</p>
                  </div>
                )}
              </div>

              {/* customerPresent is only set by a supplier portal, where staff fill this in. */}
              <MarketingOptIn
                checked={marketingOptIn}
                onChange={setMarketingOptIn}
                staff={customerPresent !== undefined}
                className="mb-5"
              />

              {/* Summary */}
              <div className="rounded-xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] px-4 py-3 mb-5 text-sm text-[var(--text-on-light-muted)] space-y-1">
                <p><span className="text-[var(--text-on-light)] font-medium">Session:</span> {selectedPackage?.label}</p>
                <p><span className="text-[var(--text-on-light)] font-medium">Date:</span> {friendlyDay(selectedDay!)}</p>
                <p><span className="text-[var(--text-on-light)] font-medium">Time:</span> {selectedSlotRaw} (45 minutes)</p>
                {selectedPackage?.includes && (
                  <p className="text-[var(--text-brand)]">{selectedPackage.includes}</p>
                )}
                <p className="font-bold text-[var(--accent)]">Total: £{selectedPackage?.price}</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto rounded-full bg-[var(--text-brand)] px-10 py-3 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Please wait…" : `Pay £${selectedPackage?.price} & Confirm`}
              </button>
              <p className="mt-3 text-xs text-[var(--text-on-light-muted)]">
                You&apos;ll be redirected to our secure SumUp payment page. A calendar invite and confirmation email follow automatically.
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
